import { Delete, Edit, SyncAlt } from '@mui/icons-material';
import { getQueryKey } from '@trpc/react-query';
import {
  createEqualisationEditorRoute,
  EQUALISATIONS_ADD,
} from 'adminConstants/routes';
import Page from 'components/new/Page';
import { useLayoutContext } from 'contexts/LayoutContext';
import { RolePermission } from 'further-types/admin-user-role';
import { ActionStatus } from 'further-types/lp/action';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { useDisclosure } from 'further-ui/hooks';
import { ActionStatusLabels } from 'further-ui/labels';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import api from 'lib/trpcClient';
import React from 'react';

const EqualisationsContent: React.FC = () => {
  const { vehicle } = useLayoutContext();
  const [canCreate, canEdit, canDelete] = useHasPermissions([
    RolePermission.LpCreateEqualisation,
    RolePermission.LpEditEqualisation,
    RolePermission.LpDeleteEqualisation,
  ]);
  const [equalisationsData] = api.equalisations.list.useSuspenseQuery({
    vehicleId: vehicle?._id ?? '',
  });

  const listEqualisationsQueryKey = getQueryKey(api.equalisations.list, {
    vehicleId: vehicle?._id ?? '',
  });

  const deleteDisclosure = useDisclosure<{ id: string }>();

  const mutationOpts = useHandleTrpcMutation<{
    success: boolean;
  }>();

  const deleteEqualisation = api.equalisations.delete.useMutation(
    mutationOpts({
      successMessage: 'Close deleted successfully.',
      invalidations: [listEqualisationsQueryKey],
      onSuccess: () => {
        deleteDisclosure.onClose();
      },
    }),
  );

  const handleDeleteEqualisation = () => {
    deleteEqualisation.mutate({
      equalisationId: deleteDisclosure.actionProps.id,
      vehicleId: vehicle?._id ?? '',
    });
  };

  const equalisations = equalisationsData?.results ?? [];

  const columns: Array<TableColumn<(typeof equalisations)[number]>> = [
    {
      key: 'name',
      label: 'Close name',
      width: 250,
      render: (row) => row.name,
    },
    {
      key: 'ledgerDate',
      label: 'Close date',
      width: 140,
      render: (row) => row.ledgerDate.toReadable(),
    },
    {
      key: 'status',
      label: 'Status',
      width: 100,
      render: (row) =>
        row.status === ActionStatus.Approved
          ? 'Submitted'
          : ActionStatusLabels[row.status],
    },
  ];

  return (
    <Page
      tabTitle="Close & Equalise: View Closes"
      actions={[
        {
          label: 'Add new',
          variant: 'contained',
          link: EQUALISATIONS_ADD,
          hidden: !canCreate,
        },
      ]}
      headingIcon={SyncAlt}
      spacing="md"
      heading="Closes"
      withHeadingDivider
    >
      <Table
        actions={[
          {
            label: 'Edit',
            icon: Edit,
            link: (row) => createEqualisationEditorRoute(row.id ?? ''),
            hidden: !canEdit,
            disabled: (row) => !row.isEditable,
          },
          {
            label: 'Delete',
            icon: Delete,
            onClick: (row) =>
              deleteDisclosure.stageAction({ id: row.id ?? '' }),
            hidden: !canDelete,
            disabled: (row) =>
              !row.canBeDeleted || deleteEqualisation.isPending,
          },
        ]}
        onRowClickHref={(row) =>
          row.isEditable && canEdit
            ? createEqualisationEditorRoute(row.id ?? '')
            : undefined
        }
        columns={columns}
        scrollable
        rows={equalisations}
      />
      <ConfirmDialog
        maxWidth="sm"
        onClose={deleteDisclosure.onClose}
        open={deleteDisclosure.isOpen}
        loading={deleteEqualisation.isPending}
        title="Deleting close"
        content="You are about to delete this fund close. This can't be undone."
        actions={{
          confirm: {
            label: 'Delete close',
            onClick: handleDeleteEqualisation,
          },
          cancel: {
            label: 'Cancel',
            onClick: deleteDisclosure.onClose,
          },
        }}
      />
    </Page>
  );
};

export default EqualisationsContent;
